import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { ArticleSummary } from '@/types'

import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { getArticles } from '@/lib/journal'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHero } from '@/components/generals/page-hero'
import { CtaPanel } from '@/components/generals/cta-panel'
import { Reveal } from '@/components/generals/reveal'
import { ArticleCard, FeaturedArticle } from '@/components/journal/article-card'

interface JournalPageProps {
    articles: Array<ArticleSummary>
    unavailable: boolean
}

const JournalPage: FC<JournalPageProps> = ({ articles, unavailable }): ReactNode => {
    const [category, setCategory] = useState('All')

    const categories = useMemo(() => {
        return ['All', ...Array.from(new Set(articles.map(article => article.category)))]
    }, [articles])

    const featured = useMemo(() => articles.find(article => article.featured) ?? articles[0] ?? null, [articles])

    const visible = useMemo(() => {
        const rest = articles.filter(article => article.id !== featured?.id)

        return category === 'All' ? rest : articles.filter(article => article.category === category)
    }, [articles, featured, category])

    const showFeatured = featured !== null && category === 'All'

    return (
        <Layout>
            <Seo
                title="Journal"
                description="Notes from the control tower: field notes on freight operations, customs and integrations from the people who run Quayside flows."
            />

            <PageHero
                label="Journal"
                titleTop="Notes from the"
                titleBottom="control tower."
                description="Field notes on freight, customs and integration from the people who run the flows every day."
            />

            <section id='notes' aria-label='Journal notes' className='section-b'>
                <div className='shell'>
                    <div role='group' aria-label='Filter notes by category' className='flex flex-wrap gap-2'>
                        {categories.map(item => {
                            const selected = item === category

                            return (
                                <button
                                    key={item}
                                    type='button'
                                    aria-pressed={selected}
                                    onClick={() => setCategory(item)}
                                    className={cn(
                                        'h-10 rounded-full border px-4 text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine focus-visible:ring-offset-2 focus-visible:ring-offset-cream',
                                        selected ? 'border-ink bg-ink text-white' : 'border-ink/15 bg-paper/70 text-ink-soft hover:border-ink/30 hover:text-ink'
                                    )}
                                >
                                    {item}
                                </button>
                            )
                        })}
                    </div>

                    {unavailable && (
                        <p className='surface mt-8 rounded-2xl px-5 py-4 text-sm text-ink-soft'>
                            The journal is refreshing right now. Please check back in a minute.
                        </p>
                    )}

                    {showFeatured && (
                        <Reveal className='mt-10'>
                            <FeaturedArticle article={featured} />
                        </Reveal>
                    )}

                    {visible.length > 0 ? (
                        <ul className='mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3'>
                            {visible.map((article, index) => (
                                <Reveal as='li' key={article.id} delay={(index % 3) * 100}>
                                    <ArticleCard article={article} />
                                </Reveal>
                            ))}
                        </ul>
                    ) : (
                        !showFeatured && !unavailable && (
                            <p className='mt-10 rounded-[24px] bg-sand px-6 py-10 text-center text-ink-soft'>
                                No notes here yet. New ones land every few weeks.
                            </p>
                        )
                    )}
                </div>
            </section>

            <CtaPanel />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<JournalPageProps> = async () => {
    try {
        const articles = await getArticles()

        return { props: { articles, unavailable: false }, revalidate: 60 }
    }
    catch (error) {
        console.error('Journal could not load:', error instanceof Error ? error.message : error)

        return { props: { articles: [], unavailable: true }, revalidate: 15 }
    }
}

export default JournalPage
